import { ChatCircleText, Sparkle, SpinnerGap, WarningCircle } from '@phosphor-icons/react';
import { useState } from 'react';
import { Button } from '@/components/ui-kit/button';
import { Textarea } from '@/components/ui-kit/textarea';
import { SegmentAiMarkdown } from './segment-ai-markdown';
import { useGenerateSegmentDraft } from '../hooks/use-segments';
import type { SegmentAiDraft } from '../types/segment-ai.types';
import type { SegmentCondition, SegmentFieldDefinition } from '../types/segments.types';
import { describeCondition, SEGMENT_AI_PROMPT_MAX_LENGTH } from '../utils/segment-ai-draft';
import { getSegmentAiErrorMessage, SegmentAiReplyError } from '../utils/segment-ai-errors';

export type SegmentAiAssistantProps = {
  fields: SegmentFieldDefinition[];
  onApply: (draft: SegmentAiDraft) => void;
};

const EXAMPLE_PROMPTS = [
  'German-speaking contacts who joined from the CSV import',
  'Contacts tagged Bern or luzern',
];

type Feedback = { kind: 'error' | 'reply'; text: string };

const SectionLabel = ({ children }: { children: string }) => (
  <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">{children}</p>
);

const ConditionList = ({
  conditions,
  combinator,
  fields,
}: {
  conditions: SegmentCondition[];
  combinator: 'and' | 'or';
  fields: SegmentFieldDefinition[];
}) => (
  <ul className="space-y-1.5">
    {conditions.map((condition, index) => (
      <li key={`${condition.field}-${index}`} className="flex items-baseline gap-2 text-[13px]">
        <span className="w-9 shrink-0 text-[11px] font-semibold uppercase text-neutral-500">
          {index === 0 ? 'If' : combinator}
        </span>
        <span className="rounded-md bg-white px-2 py-1 text-neutral-900 ring-1 ring-neutral-200">
          {describeCondition(condition, fields)}
        </span>
      </li>
    ))}
  </ul>
);

export const SegmentAiAssistant = ({ fields, onApply }: SegmentAiAssistantProps) => {
  const { mutateAsync: generate, isPending } = useGenerateSegmentDraft();
  const [prompt, setPrompt] = useState('');
  const [draft, setDraft] = useState<SegmentAiDraft | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  const trimmed = prompt.trim();
  const canGenerate = trimmed.length > 0 && fields.length > 0 && !isPending;

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setFeedback(null);
    setDraft(null);
    try {
      setDraft(await generate({ prompt: trimmed, fields }));
    } catch (caught) {
      setFeedback(
        caught instanceof SegmentAiReplyError
          ? { kind: 'reply', text: caught.reply }
          : { kind: 'error', text: getSegmentAiErrorMessage(caught) }
      );
    }
  };

  return (
    <section
      aria-label="Generate with AI"
      className="rounded-lg border border-primary-100 bg-primary-50/40 p-4"
    >
      <div className="flex items-center gap-2">
        <Sparkle className="size-4 text-primary-600" weight="fill" aria-hidden />
        <h3 className="text-sm font-semibold text-neutral-900">Describe your audience</h3>
      </div>
      <p className="mt-0.5 text-xs text-neutral-500">
        Tell us who to target and AI drafts the rules. You review it before anything is created.
      </p>

      <Textarea
        value={prompt}
        rows={3}
        maxLength={SEGMENT_AI_PROMPT_MAX_LENGTH}
        placeholder={`e.g. ${EXAMPLE_PROMPTS[0]}`}
        aria-label="Describe the segment you want"
        className="mt-3 resize-none border-neutral-300 bg-white"
        disabled={isPending}
        onChange={(event) => setPrompt(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) void handleGenerate();
        }}
      />

      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5">
          {EXAMPLE_PROMPTS.map((example) => (
            <button
              key={example}
              type="button"
              disabled={isPending}
              className="rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-[11px] text-neutral-700 hover:border-primary-300 disabled:opacity-50"
              onClick={() => setPrompt(example)}
            >
              {example}
            </button>
          ))}
        </div>
        <Button
          type="button"
          size="sm"
          className="h-8 bg-primary-600 px-3 text-[13px] text-white hover:bg-primary-700"
          disabled={!canGenerate}
          onClick={() => void handleGenerate()}
        >
          {isPending ? (
            <>
              <SpinnerGap className="size-4 animate-spin" aria-hidden />
              Generating…
            </>
          ) : (
            'Generate'
          )}
        </Button>
      </div>

      {feedback?.kind === 'error' ? (
        <div
          role="alert"
          className="mt-3 flex items-start gap-2 rounded-lg bg-error-background px-3 py-2.5"
        >
          <WarningCircle className="mt-0.5 size-4 shrink-0 text-error" aria-hidden />
          <p className="text-xs text-error">{feedback.text}</p>
        </div>
      ) : null}

      {feedback?.kind === 'reply' ? (
        <div
          role="status"
          className="mt-3 flex items-start gap-2 rounded-lg border border-neutral-200 bg-white px-3 py-2.5"
        >
          <ChatCircleText className="mt-0.5 size-4 shrink-0 text-primary-600" aria-hidden />
          <div className="min-w-0">
            <SectionLabel>The agent replied</SectionLabel>
            <SegmentAiMarkdown text={feedback.text} className="mt-1" />
            <p className="mt-2 text-xs text-neutral-500">
              Add the missing detail above and generate again.
            </p>
          </div>
        </div>
      ) : null}

      {draft ? (
        <div className="mt-3 space-y-4 rounded-lg border border-neutral-200 bg-white p-4">
          <div>
            <SectionLabel>Draft segment</SectionLabel>
            <p className="mt-1 text-sm font-semibold text-neutral-900">
              {draft.name || 'Untitled segment'}
            </p>
            {draft.description ? (
              <p className="mt-0.5 text-[13px] text-neutral-600">{draft.description}</p>
            ) : null}
          </div>

          <div className="space-y-2.5 rounded-md bg-neutral-50 p-3">
            <ConditionList
              conditions={draft.rules.conditions}
              combinator={draft.rules.combinator}
              fields={fields}
            />
            {draft.rules.groups.map((group, index) => (
              <div key={index} className="space-y-1.5 border-l-2 border-neutral-300 pl-3">
                <p className="text-[11px] font-semibold uppercase text-neutral-500">
                  {draft.rules.combinator} match {group.combinator === 'or' ? 'any' : 'all'} of
                </p>
                <ConditionList
                  conditions={group.conditions}
                  combinator={group.combinator}
                  fields={fields}
                />
              </div>
            ))}
          </div>

          {draft.warnings.length > 0 ? (
            <div className="rounded-md bg-warning-background px-3 py-2.5">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-warning">
                Not included
              </p>
              <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs text-neutral-800">
                {draft.warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {draft.assumptions.length > 0 ? (
            <div>
              <SectionLabel>Assumptions</SectionLabel>
              <ul className="mt-1 list-disc space-y-1 pl-5 text-[13px] leading-5 text-neutral-700">
                {draft.assumptions.map((assumption) => (
                  <li key={assumption}>{assumption}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {draft.summary ? (
            <div className="border-t border-neutral-100 pt-3">
              <SectionLabel>How the agent read this</SectionLabel>
              <SegmentAiMarkdown text={draft.summary} className="mt-1.5" />
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-2 border-t border-neutral-100 pt-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-3 text-[13px]"
              onClick={() => setDraft(null)}
            >
              Discard
            </Button>
            <Button
              type="button"
              size="sm"
              className="h-8 bg-primary-600 px-3 text-[13px] text-white hover:bg-primary-700"
              onClick={() => {
                onApply(draft);
                setDraft(null);
              }}
            >
              Use this segment
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
};
